import { useEffect, useState } from "react";

export const NMS_CHART_COLORS = [
  "#0284C7",
  "#059669",
  "#D97706",
  "#7C3AED",
  "#0EA5E9",
  "#DC2626",
  "#10B981",
  "#F59E0B",
];

export const PORT_USAGE_COLORS = {
  usingPower: "#059669",
  notUsingPower: "#0284C7",
  disabled: "#FB7185",
  error: "#D97706",
  free: "#94A3B8",
};

export const readCssVar = (name, fallback) => {
  if (typeof window === "undefined") return fallback;
  const value = getComputedStyle(document.documentElement)
    .getPropertyValue(name)
    .trim();
  return value || fallback;
};

export const formatChartTick = (value, duration) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const isDaily = duration === "7d" || duration === "30d";
  return date.toLocaleString("en-IN",
    isDaily
      ? { day: "2-digit", month: "short" }
      : { hour: "2-digit", minute: "2-digit", hour12: false }
  );
};

export const hasChartSeries = (series) =>
  Array.isArray(series) &&
  series.some((item) =>
    Array.isArray(item?.data) ? item.data.length > 0 : item != null
  );

export const useDocumentTheme = () => {
  const [theme, setTheme] = useState(() =>
    typeof document === "undefined"
      ? "light"
      : document.documentElement.getAttribute("data-theme") || "light"
  );

  useEffect(() => {
    const root = document.documentElement;
    const apply = () => setTheme(root.getAttribute("data-theme") || "light");
    const observer = new MutationObserver(apply);
    observer.observe(root, { attributes: true, attributeFilter: ["data-theme"] });
    return () => observer.disconnect();
  }, []);

  return theme;
};

export const getApexTheme = ({
  duration,
  yTitle,
  yFormatter,
  tooltipY,
  tickAmount,
  type = "line",
  xaxisType = "datetime",
} = {}) => {
  const muted = readCssVar("--inc-info-color", "#64748B");
  const border = readCssVar("--inc-border", "#E2E8F0");
  const title = readCssVar("--inc-card-title-color", "#0F172A");
  const isDark =
    typeof document !== "undefined" &&
    document.documentElement.getAttribute("data-theme") === "dark";

  return {
    chart: {
      type,
      toolbar: { show: false },
      zoom: { enabled: false },
      fontFamily: readCssVar("--inc-font", "Inter, sans-serif"),
      foreColor: muted,
      background: "transparent",
    },
    colors: NMS_CHART_COLORS,
    stroke: { width: type === "bar" ? 0 : 2, curve: "smooth" },
    dataLabels: { enabled: false },
    grid: { borderColor: border, strokeDashArray: 3 },
    legend: {
      position: "bottom",
      horizontalAlign: "center",
      showForSingleSeries: true,
      labels: { colors: muted },
    },
    tooltip: {
      theme: isDark ? "dark" : "light",
      shared: true,
      intersect: false,
      x: {
        show: true,
        format: duration === "7d" || duration === "30d" ? "dd MMM" : "HH:mm",
        formatter: (value) =>
          xaxisType === "datetime" ? formatChartTick(value, duration) : value,
      },
      y: {
        formatter: tooltipY || ((val) => val),
        title: {
          formatter: (seriesName) => (seriesName ? `${seriesName}: ` : ""),
        },
      },
    },
    xaxis: {
      type: xaxisType,
      tickAmount:
        tickAmount ?? (duration === "7d" || duration === "30d" ? 6 : 8),
      labels: {
        formatter: (value) =>
          xaxisType === "datetime" ? formatChartTick(value, duration) : value,
        style: { colors: muted, fontSize: "11px" },
      },
      axisBorder: { show: false },
      axisTicks: { show: false },
      tooltip: { enabled: false },
    },
    yaxis: {
      min: 0,
      labels: {
        formatter:
          yFormatter ||
          ((val) => (val == null ? "0" : Number(val).toFixed(0))),
        style: { colors: muted, fontSize: "11px" },
      },
      ...(yTitle
        ? {
            title: {
              text: yTitle,
              style: { color: title, fontSize: "11px", fontWeight: 500 },
            },
          }
        : {}),
    },
  };
};
